import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges
} from '@angular/core';
import { LoRadioGroup, LoRadioOption } from 'lo-design-system';

/**
 * Monthly / Yearly billing-period switch shared by the settings plans page, the
 * public pricing page and the Social Listening onboarding page. Two-way bindable
 * via `[(annually)]`; the parent reacts to {@link annuallyChange} to re-filter
 * its plans.
 *
 * A `lo-radio-group` in a row: a billing period is the lo example of two options
 * whose wording has to be read side by side. The saving cannot be an element of
 * its own beside the title any more, so it joins the yearly option's label
 * ("Yearly — 2 months free").
 */
@Component({
	standalone: true,
	selector: 'app-plan-interval-toggle',
	template: `
		<lo-radio-group
			ariaLabel="Billing period"
			direction="row"
			[options]="options"
			[value]="annually ? 'yearly' : 'monthly'"
			(valueChange)="onChange($event)"
		></lo-radio-group>
	`,
	imports: [LoRadioGroup]
})
export class PlanIntervalToggleComponent implements OnChanges {
	@Input() annually = false;

	/**
	 * Annual saving %, stated on the yearly option. Defaults to 30 so a caller
	 * that does not bind it keeps the label it had; pass the product's real
	 * computed saving (see `computeAnnualSavings`). 0 drops the tag.
	 */
	@Input() savingsPercent = 30;

	/**
	 * Whole months the year gives away, when the term is a whole number of
	 * monthly payments — the application ladder's year costs ten months, so this
	 * is 2 and the option's tag reads "2 months free", which is what the
	 * signed-off page says and what a brand can check against the annual total
	 * on the card. `null` (the default) falls back to {@link savingsPercent},
	 * which is the only thing that can be said about a term that does not
	 * divide — and which is why the tag is phrased "Save 30%" rather than
	 * "30%": both readings have to stand on their own beside the word Yearly.
	 */
	@Input() freeMonths: number | null = null;

	@Output() annuallyChange = new EventEmitter<boolean>();

	/** Built here rather than in a lifecycle hook so a caller that binds no
	 *  input at all still renders a switch — `ngOnChanges` never fires for one. */
	options: LoRadioOption[] = this.buildOptions();

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['savingsPercent'] || changes['freeMonths']) {
			this.options = this.buildOptions();
		}
	}

	onChange(value: string | null): void {
		const annually = value === 'yearly';
		if (annually === this.annually) {
			return;
		}
		this.annually = annually;
		this.annuallyChange.emit(annually);
	}

	private buildOptions(): LoRadioOption[] {
		const tag = this.yearlyTag();
		return [
			{ value: 'monthly', label: 'Monthly' },
			{ value: 'yearly', label: tag ? `Yearly — ${tag}` : 'Yearly' }
		];
	}

	private yearlyTag(): string | null {
		if (this.freeMonths !== null && this.freeMonths > 0) {
			const months = this.freeMonths === 1 ? 'month' : 'months';
			return `${this.freeMonths} ${months} free`;
		}
		return this.savingsPercent > 0 ? `Save ${this.savingsPercent}%` : null;
	}
}
